import { MAX_BATCH } from "@larder/shared";
import { useEffect, useRef } from "react";
import { enrichItems } from "./enrich";
import type { TrackedItem } from "./items";

/**
 * Fills in what is missing, quietly, whenever something is missing it.
 *
 * Deliberately driven off the ITEM LIST rather than off the add screen. Items
 * arrive by four routes — typed, spoken, scanned, and restored from a previous
 * install — and hanging enrichment off each of them would mean four places to
 * forget. Watching for `enrichedAt === null` covers all four by construction,
 * and backfills everything added before this feature existed as a free
 * side effect.
 *
 * The whole thing is fire-and-forget. It writes nothing the user is waiting on
 * and reports nothing when it fails.
 */

/**
 * How long to wait for the list to settle before asking.
 *
 * A spoken shopping list lands as one batch write but arrives as several
 * snapshots, and a scan lands seconds after the item it belongs to. Waiting a
 * beat turns what would be three or four calls into one — which matters because
 * the batch is where the cost saving lives, not the call.
 */
const SETTLE_MS = 800;

export function useEnrichment(
  locationId: string | null,
  items: TrackedItem[],
  timezone: string,
): void {
  /**
   * Items we have already asked about this session.
   *
   * A successful enrichment stamps `enrichedAt` and so removes itself from the
   * queue. A FAILED one does not — by design, so it is retried on the next app
   * load — which without this guard would spin: snapshot, ask, fail, snapshot.
   * Session-scoped, so a reload is still a fresh attempt.
   */
  const attempted = useRef(new Set<string>());
  const inFlight = useRef(false);

  useEffect(() => {
    if (!locationId) return;

    const timer = setTimeout(() => {
      if (inFlight.current) return;

      const todo = items
        .filter((i) => i.enrichedAt === null && !attempted.current.has(i.id))
        // The server caps the batch anyway; capping here too means the leftovers
        // stay unmarked and get picked up by the next pass rather than being
        // silently dropped on the floor.
        .slice(0, MAX_BATCH);

      if (todo.length === 0) return;

      inFlight.current = true;
      for (const i of todo) attempted.current.add(i.id);

      void enrichItems(locationId, todo, timezone).finally(() => {
        inFlight.current = false;
      });
    }, SETTLE_MS);

    return () => clearTimeout(timer);
  }, [locationId, items, timezone]);
}
